import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Users,
  Pencil,
  Trash2,
  Mail,
  MailCheck,
  Eye,
  EyeOff,
  CheckCircle2,
  X,
  Send,
  UserPlus,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import {
  residents as api,
  units as unitsApi,
  residentImport,
  type Resident,
  type Unit,
  type ApiError,
  type CsvImportResult,
} from "../../services/api";
import DataTable from "../../components/DataTable";
import SearchSelect from "../../components/SearchSelect";
import BulkImportExport from "../../components/BulkImportExport";
import StatusFilterBar from "../../components/StatusFilterBar";
import { useServerTable } from "../../hooks/useServerTable";
import { useStatusCounts } from "../../hooks/useStatusCounts";
import { downloadBlob } from "../../utils/downloadBlob";

const RESIDENT_STATUS_FILTERS = ["all", "active", "inactive"] as const;
type ResidentStatusFilter = (typeof RESIDENT_STATUS_FILTERS)[number];

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};
const actionBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px 6px",
  borderRadius: 6,
  color: "var(--nx-text-muted)",
  display: "inline-flex",
  alignItems: "center",
};

const INIT = {
  unitId: "",
  fullName: "",
  email: "",
  phone: "",
  type: "owner",
  moveInDate: "",
  moveOutDate: "",
};

const INVITE_INIT = { secretaryEmail: "", secretaryPassword: "" };

const typeColor = (t: string) =>
  t === "owner"
    ? { bg: "rgba(26,108,246,0.1)", text: "#1A6CF6" }
    : t === "tenant"
      ? { bg: "rgba(109,40,217,0.1)", text: "#6D28D9" }
      : { bg: "rgba(16,185,129,0.1)", text: "#10B981" };

type BulkEntry = { resident: Resident; selected: boolean };
type ResidentRow = Resident & { __familyCount?: number; __nested?: boolean };

const ResidentsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const societyCode = user?.societyCode ?? null;
  // Residents only see their own household (enforced server-side too) and can't
  // create/edit/delete/import/invite — this page is admin/staff-only for those actions.
  const isResidentRole = user?.role === "RESIDENT" || user?.role === "TENANT";

  const [statusFilter, setStatusFilter] = useState<ResidentStatusFilter>("all");

  const {
    rows,
    totalElements,
    totalPages,
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortField,
    sortDir,
    setSort,
    sort,
    q,
    setQ,
    refetch,
  } = useServerTable<Resident>(
    (params) =>
      societyId
        ? api.list(societyId, {
            ...params,
            status: statusFilter === "all" ? undefined : statusFilter,
          })
        : Promise.resolve({ content: [], totalElements: 0, totalPages: 0 }),
    [societyId, statusFilter],
  );

  const {
    counts: statusCounts,
    loading: statusCountsLoading,
    refetch: refetchStatusCounts,
  } = useStatusCounts(
    RESIDENT_STATUS_FILTERS,
    (key) =>
      societyId
        ? api
            .list(societyId, {
              page: 0,
              size: 1,
              status: key === "all" ? undefined : key,
            })
            .then((r) => r.totalElements)
        : Promise.resolve(0),
    [societyId],
  );

  const [unitList, setUnitList] = useState<Unit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Resident | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [active, setActive] = useState(true);
  // Set when "Add Family Member" is opened from a resident row — locks the Unit
  // field to that resident's (already-occupied) unit instead of the normal picker,
  // which only lists vacant units.
  const [lockedUnitLabel, setLockedUnitLabel] = useState<string | null>(null);
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmResident, setConfirmResident] = useState<Resident | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [importResult, setImportResult] = useState<CsvImportResult | null>(
    null,
  );
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedResidentIds, setSelectedResidentIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);

  // Single invite modal
  const [inviteResident, setInviteResident] = useState<Resident | null>(null);
  const [inviteForm, setInviteForm] = useState(INVITE_INIT);
  const [showInvitePwd, setShowInvitePwd] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState("");

  // Bulk invite modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkEntries, setBulkEntries] = useState<BulkEntry[]>([]);
  const [bulkForm, setBulkForm] = useState(INVITE_INIT);
  const [showBulkPwd, setShowBulkPwd] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkDone, setBulkDone] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkSentCount, setBulkSentCount] = useState(0);

  const loadUnits = () => {
    if (!societyId) return;
    setUnitsLoading(true);
    setUnitsError(false);
    unitsApi
      .list(societyId, { status: "vacant", size: 500 })
      .then((page) => setUnitList(page.content))
      .catch(() => setUnitsError(true))
      .finally(() => setUnitsLoading(false));
  };

  useEffect(() => {
    if (!showForm || !societyId) return;
    loadUnits();
  }, [showForm, societyId]);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const openAdd = () => {
    setEditing(null);
    setFormError("");
    setForm(INIT);
    setActive(true);
    setLockedUnitLabel(null);
    setShowForm(true);
  };

  const openAddFamilyMember = (r: Resident) => {
    setEditing(null);
    setFormError("");
    // Family members typically move in/out alongside the primary occupant, so default
    // to their dates instead of leaving the fields blank — still fully editable.
    setForm({
      ...INIT,
      unitId: r.unitId,
      type: "family",
      moveInDate: r.moveInDate ?? "",
      moveOutDate: r.moveOutDate ?? "",
    });
    setActive(true);
    setLockedUnitLabel(
      r.blockName ? `${r.blockName} — ${r.unitNumber ?? ""}` : (r.unitNumber ?? ""),
    );
    setShowForm(true);
  };

  const toggleUnitExpanded = (unitId: string) => {
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  };

  const handleImport = async (file: File) => {
    if (!societyId) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await residentImport.importCsv(societyId, file);
      setImportResult(result);
      refetch();
      refetchStatusCounts();
      setSelectedResidentIds([]);
    } catch (err) {
      setImportResult({
        created: 0,
        failed: 1,
        errors: [(err as ApiError).message || "Import failed"],
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    if (!societyId) return;
    setExporting(true);
    try {
      const blob = await api.exportCsv(societyId, sort);
      downloadBlob(blob, "residents-export.csv");
    } catch {
      /* ignore */
    } finally {
      setExporting(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedResidentIds.length === 0) return;
    setDeleteError("");
    setConfirmBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (!societyId || selectedResidentIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await api.bulkDelete(societyId, selectedResidentIds);
      refetch();
      refetchStatusCounts();
      setSelectedResidentIds([]);
      setConfirmBulkDeleteOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setDeleteError(message || "Failed to delete selected residents. Please try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const openEdit = (r: Resident) => {
    setEditing(r);
    setFormError("");
    setForm({
      unitId: r.unitId,
      fullName: r.fullName,
      email: r.email ?? "",
      phone: r.phone ?? "",
      type: r.type,
      moveInDate: r.moveInDate ?? "",
      moveOutDate: r.moveOutDate ?? "",
    });
    setActive(r.active);
    setLockedUnitLabel(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(INIT);
    setActive(true);
    setFormError("");
    setLockedUnitLabel(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    if (!form.unitId) {
      setFormError("Please select a unit.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const payload = {
        unitId: form.unitId,
        fullName: form.fullName,
        type: form.type as Resident["type"],
        email: form.email || undefined,
        phone: form.phone || undefined,
        moveInDate: form.moveInDate || undefined,
        moveOutDate: form.moveOutDate || undefined,
        active,
      };
      if (editing) {
        await api.update(societyId, editing.id, payload);
      } else {
        await api.create(societyId, payload);
      }
      refetch();
      refetchStatusCounts();
      closeForm();
    } catch (err) {
      setFormError(
        (err as ApiError).message ||
          "Failed to save resident. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (r: Resident) => {
    setConfirmResident(r);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    if (!societyId || !confirmResident) return;
    setDeletingId(confirmResident.id);
    try {
      await api.delete(societyId, confirmResident.id);
      refetch();
      refetchStatusCounts();
      setSelectedResidentIds((current) =>
        current.filter((id) => id !== confirmResident.id),
      );
      setConfirmResident(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setDeleteError(message || "Failed to delete resident. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Single invite ── */
  const openInvite = (r: Resident) => {
    setInviteResident(r);
    setInviteForm(INVITE_INIT);
    setShowInvitePwd(false);
    setInviteError("");
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !inviteResident) return;
    if (!inviteResident.email) {
      setInviteError(
        "This resident has no email address on file. Edit the resident first to add one.",
      );
      return;
    }
    if (!inviteForm.secretaryEmail) {
      setInviteError("Your email address is required to send the invite.");
      return;
    }
    if (!inviteForm.secretaryPassword) {
      setInviteError("Your email password is required to send the invite.");
      return;
    }
    setInviteSaving(true);
    setInviteError("");
    try {
      await api.sendInvite(societyId, inviteResident.id, inviteForm);
      refetch();
      setInviteResident(null);
    } catch (err) {
      setInviteError(
        (err as ApiError).message ||
          "Failed to send invite. Check your email credentials and try again.",
      );
    } finally {
      setInviteSaving(false);
    }
  };

  /* ── Bulk invite ── */
  const openBulkInvite = () => {
    setBulkEntries(
      rows
        .filter((r) => r.email && !r.username)
        .map((r) => ({
          resident: r,
          selected: !r.inviteSentAt,
        })),
    );
    setBulkForm(INVITE_INIT);
    setShowBulkPwd(false);
    setBulkDone(false);
    setBulkError("");
    setBulkSentCount(0);
    setShowBulkModal(true);
  };

  const handleSendBulkInvites = async () => {
    if (!societyId) return;
    const toInvite = bulkEntries.filter((e) => e.selected);
    if (!toInvite.length) {
      setBulkError("Select at least one resident.");
      return;
    }
    if (!bulkForm.secretaryEmail) {
      setBulkError("Your email address is required.");
      return;
    }
    if (!bulkForm.secretaryPassword) {
      setBulkError("Your email password is required.");
      return;
    }
    setBulkSaving(true);
    setBulkError("");
    try {
      const result = await api.bulkSendInvites(societyId, {
        residentIds: toInvite.map((e) => e.resident.id),
        secretaryEmail: bulkForm.secretaryEmail,
        secretaryPassword: bulkForm.secretaryPassword,
      });
      refetch();
      setBulkSentCount(result.count);
      setBulkDone(true);
    } catch (err) {
      setBulkError(
        (err as ApiError).message ||
          "Failed to send invites. Check your email credentials and try again.",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const unitLabel = (u: Unit) =>
    u.blockName
      ? `${u.blockName} — ${u.unitNumber}${u.floor != null ? ` (Floor ${u.floor})` : ""}`
      : `${u.unitNumber}${u.floor != null ? ` (Floor ${u.floor})` : ""}`;

  // The vacant-units fetch excludes the resident's own current unit (it's occupied by
  // them), so it's added back in directly from the resident record itself — which already
  // carries unitNumber/blockName from the list API — rather than a second network call, so
  // the current unit is guaranteed to appear even if that fetch were ever slow/racy.
  const unitOptions = useMemo(() => {
    const sorted = [...unitList].sort((a, b) => {
      const blockCmp = (a.blockName || "").localeCompare(b.blockName || "", undefined, {
        numeric: true,
        sensitivity: "base",
      });
      if (blockCmp !== 0) return blockCmp;
      return a.unitNumber.localeCompare(b.unitNumber, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
    const opts = sorted.map((u) => ({ value: u.id, label: unitLabel(u) }));
    if (editing?.unitId && !opts.some((o) => o.value === editing.unitId)) {
      opts.unshift({
        value: editing.unitId,
        label: editing.blockName
          ? `${editing.blockName} — ${editing.unitNumber ?? ""}`
          : editing.unitNumber ?? "Current unit",
      });
    }
    return opts;
  }, [unitList, editing]);

  // Groups same-unit residents (within the currently loaded page) under one collapsible
  // row — an owner/tenant shown as primary, family members hidden behind a chevron toggle.
  const displayRows = useMemo<ResidentRow[]>(() => {
    const byUnit = new Map<string, Resident[]>();
    for (const r of rows) {
      if (!r.unitId) continue;
      const arr = byUnit.get(r.unitId) ?? [];
      arr.push(r);
      byUnit.set(r.unitId, arr);
    }
    const seen = new Set<string>();
    const out: ResidentRow[] = [];
    for (const r of rows) {
      const group = r.unitId ? byUnit.get(r.unitId) : undefined;
      if (!group || group.length <= 1) {
        out.push(r);
        continue;
      }
      if (seen.has(r.unitId)) continue;
      seen.add(r.unitId);
      const primary =
        group.find((m) => m.type === "owner") ??
        group.find((m) => m.type === "tenant") ??
        group[0];
      const others = group.filter((m) => m.id !== primary.id);
      out.push({ ...primary, __familyCount: others.length });
      if (expandedUnits.has(r.unitId)) {
        for (const m of others) out.push({ ...m, __nested: true });
      }
    }
    return out;
  }, [rows, expandedUnits]);

  const selectedCount = bulkEntries.filter((e) => e.selected).length;
  const noInviteCount = bulkEntries.filter(
    (e) => !e.resident.username && !e.resident.inviteSentAt,
  ).length;
  const noEmailCount = rows.filter((r) => !r.email).length;

  return (
    <PageLayout>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "var(--nx-text-primary)",
              letterSpacing: "-0.03em",
              marginBottom: 4,
            }}
          >
            Residents
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {totalElements} registered residents
          </p>
        </div>
        {!isResidentRole && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <BulkImportExport
              onImport={handleImport}
              onExport={handleExport}
              importing={importing}
              exporting={exporting}
            />
            <button
              onClick={handleBulkDelete}
              className="nx-btn nx-btn-outline"
              style={{ gap: 6 }}
              disabled={selectedResidentIds.length === 0}
            >
              <Trash2 size={14} /> Delete selected
            </button>
            <button
              onClick={openBulkInvite}
              className="nx-btn nx-btn-outline"
              style={{ gap: 6 }}
            >
              <Send size={14} /> Send Invites
            </button>
            <button
              onClick={openAdd}
              className="nx-btn nx-btn-primary"
              style={{ gap: 6 }}
            >
              <Plus size={14} /> Add Resident
            </button>
          </div>
        )}
      </div>

      {importResult && (
        <div
          style={{
            padding: "10px 16px",
            background: "rgba(34,197,94,0.08)",
            border: "1px solid rgba(34,197,94,0.25)",
            borderRadius: 10,
            fontSize: 13,
            color: "#16A34A",
            marginBottom: 16,
          }}
        >
          Imported {importResult.created} residents
          {importResult.failed > 0
            ? `, ${importResult.failed} failed: ${importResult.errors.join("; ")}`
            : "."}
        </div>
      )}

      {!societyId && !loading && (
        <div
          style={{
            padding: "14px 18px",
            background: "rgba(245,158,11,0.08)",
            border: "1px solid rgba(245,158,11,0.25)",
            borderRadius: 10,
            fontSize: 13,
            color: "#D97706",
            marginBottom: 20,
          }}
        >
          No society is linked to your account. Data cannot be loaded.
        </div>
      )}

      <StatusFilterBar
        options={RESIDENT_STATUS_FILTERS.map((f) => ({
          key: f,
          label: f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1),
        }))}
        active={statusFilter}
        onChange={setStatusFilter}
        counts={statusCounts}
        countsLoading={statusCountsLoading}
      />

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            {
              key: "name",
              header: "Name",
              cellStyle: { fontWeight: 500 },
              sortable: true,
              sortKey: "fullName",
              render: (r: ResidentRow) => (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {r.__nested && <span style={{ width: 14, flexShrink: 0 }} />}
                  {!!r.__familyCount && (
                    <button
                      type="button"
                      onClick={() => toggleUnitExpanded(r.unitId)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        display: "inline-flex",
                        color: "var(--nx-text-muted)",
                        flexShrink: 0,
                      }}
                      title={
                        expandedUnits.has(r.unitId)
                          ? "Collapse family members"
                          : `Show ${r.__familyCount} family member${r.__familyCount === 1 ? "" : "s"}`
                      }
                    >
                      {expandedUnits.has(r.unitId) ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </button>
                  )}
                  <span>{r.fullName}</span>
                  {!!r.__familyCount && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: "var(--nx-text-muted)",
                        background: "rgba(148,163,184,0.1)",
                        padding: "1px 6px",
                        borderRadius: 99,
                        flexShrink: 0,
                      }}
                    >
                      +{r.__familyCount} family
                    </span>
                  )}
                </span>
              ),
            },
            {
              key: "unit",
              header: "Block / Unit",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              sortKey: "u.unitNumber",
              render: (r: Resident) =>
                r.unitNumber
                  ? r.blockName
                    ? `${r.blockName} — ${r.unitNumber}`
                    : r.unitNumber
                  : "—",
            },
            {
              key: "ids",
              header: "User ID / Society ID",
              cellStyle: { color: "var(--nx-text-muted)", fontSize: 11, fontFamily: "monospace" },
              sortable: true,
              sortKey: "username",
              render: (r: Resident) => (
                <span title={`Resident record ID: ${r.id}\nSociety record ID: ${r.societyId}`}>
                  {r.username || "Not set"} / {societyCode || r.societyId.slice(0, 8) + "…"}
                </span>
              ),
            },
            {
              key: "email",
              header: "Email",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              sortKey: "email",
              render: (r: Resident) => r.email || "—",
            },
            {
              key: "phone",
              header: "Phone",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              sortKey: "phone",
              render: (r: Resident) => r.phone || "—",
            },
            {
              key: "type",
              header: "Type",
              sortable: true,
              sortKey: "type",
              render: (r: Resident) => {
                const tc = typeColor(r.type);
                return (
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 99,
                      fontSize: 10,
                      fontWeight: 500,
                      background: tc.bg,
                      color: tc.text,
                    }}
                  >
                    {r.type.charAt(0).toUpperCase() + r.type.slice(1)}
                  </span>
                );
              },
            },
            {
              key: "moveIn",
              header: "Move-in",
              cellStyle: { color: "var(--nx-text-muted)", whiteSpace: "nowrap" },
              sortable: true,
              sortKey: "moveInDate",
              render: (r: Resident) => r.moveInDate || "—",
            },
            {
              key: "moveOut",
              header: "Move-out",
              cellStyle: { color: "var(--nx-text-muted)", whiteSpace: "nowrap" },
              sortable: true,
              sortKey: "moveOutDate",
              render: (r: Resident) => r.moveOutDate || "—",
            },
            {
              key: "login",
              header: "Portal Access",
              render: (r: Resident) => {
                if (r.username) {
                  return (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 99,
                        fontSize: 10,
                        fontWeight: 500,
                        background: "rgba(16,185,129,0.1)",
                        color: "#10B981",
                      }}
                    >
                      Active
                    </span>
                  );
                }
                if (r.inviteSentAt) {
                  return (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 99,
                        fontSize: 10,
                        fontWeight: 500,
                        background: "rgba(245,158,11,0.1)",
                        color: "#D97706",
                      }}
                    >
                      Invite Sent
                    </span>
                  );
                }
                return (
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 99,
                      fontSize: 10,
                      fontWeight: 500,
                      background: "rgba(148,163,184,0.08)",
                      color: "var(--nx-text-muted)",
                    }}
                  >
                    Not Invited
                  </span>
                );
              },
            },
            {
              key: "status",
              header: "Status",
              sortable: true,
              sortKey: "active",
              render: (r: Resident) => (
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 99,
                    fontSize: 10,
                    fontWeight: 500,
                    background: r.active
                      ? "rgba(16,185,129,0.1)"
                      : "rgba(148,163,184,0.1)",
                    color: r.active ? "#10B981" : "#64748B",
                  }}
                >
                  {r.active ? "Active" : "Inactive"}
                </span>
              ),
            },
            {
              key: "actions",
              header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (r: Resident) =>
                isResidentRole ? null : (
                <>
                  <button
                    onClick={() => openAddFamilyMember(r)}
                    style={actionBtn}
                    title="Add family member to this unit"
                  >
                    <UserPlus size={14} />
                  </button>
                  <button
                    onClick={() => openEdit(r)}
                    style={actionBtn}
                    title="Edit resident"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => (r.email && !r.username ? openInvite(r) : undefined)}
                    disabled={!r.email || !!r.username}
                    style={{
                      ...actionBtn,
                      color: !r.email || r.username
                        ? "var(--nx-border)"
                        : r.inviteSentAt
                          ? "#10B981"
                          : "var(--nx-blue)",
                      cursor: r.email && !r.username ? "pointer" : "not-allowed",
                    }}
                    title={
                      !r.email
                        ? "No email on file — edit resident to add one"
                        : r.username
                          ? "Account already activated — resident can reset their own password from the login page"
                          : r.inviteSentAt
                            ? "Resend invite"
                            : "Send invite"
                    }
                  >
                    {r.username || r.inviteSentAt ? (
                      <MailCheck size={14} />
                    ) : (
                      <Mail size={14} />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(r)}
                    disabled={deletingId === r.id}
                    style={{
                      ...actionBtn,
                      color: "#EF4444",
                      opacity: deletingId === r.id ? 0.4 : 1,
                    }}
                    title="Delete resident"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              ),
            },
          ]}
          rows={displayRows}
          rowKey={(r) => r.id}
          rowStyle={(r: ResidentRow) => (r.__nested ? { background: "var(--nx-surface-2)" } : {})}
          loading={loading}
          emptyIcon={
            <Users size={32} style={{ color: "var(--nx-text-muted)" }} />
          }
          emptyText="No residents yet. Add your first resident."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={setSort}
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Search residents…"
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          selectedRowIds={isResidentRole ? undefined : selectedResidentIds}
          onSelectionChange={isResidentRole ? undefined : setSelectedResidentIds}
          toolbarActions={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {selectedResidentIds.length > 0 && (
                <span
                  style={{ fontSize: 12, color: "var(--nx-text-secondary)" }}
                >
                  {selectedResidentIds.length} selected
                </span>
              )}
            </div>
          }
        />
      </div>

      <ConfirmDialog
        open={confirmResident !== null}
        variant="danger"
        title={`Delete "${confirmResident?.fullName}"?`}
        message="This resident will be permanently removed. This action cannot be undone."
        confirmLabel="Delete Resident"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmResident(null);
          setDeleteError("");
        }}
      />
      <ConfirmDialog
        open={confirmBulkDeleteOpen}
        variant="danger"
        title="Delete selected residents?"
        message={`This will permanently remove ${selectedResidentIds.length} selected resident${selectedResidentIds.length !== 1 ? "s" : ""}. This action cannot be undone.`}
        confirmLabel="Delete Residents"
        isLoading={bulkDeleting}
        error={deleteError}
        onConfirm={confirmBulkDelete}
        onCancel={() => {
          setConfirmBulkDeleteOpen(false);
          setDeleteError("");
        }}
      />

      {/* ── Add / Edit Resident Modal ── */}
      {showForm && (
        <Modal
          title={editing ? "Edit Resident" : "Add Resident"}
          submitLabel={editing ? "Save Changes" : "Add Resident"}
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Full Name *</label>
            <input
              name="fullName"
              value={form.fullName}
              onChange={change}
              required
              placeholder="Swetha Madhan"
              style={inp}
              autoFocus
            />
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Unit *</label>
              {lockedUnitLabel ? (
                <div
                  style={{
                    ...inp,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: "var(--nx-text-secondary)",
                  }}
                >
                  <span>{lockedUnitLabel}</span>
                  <button
                    type="button"
                    onClick={() => setLockedUnitLabel(null)}
                    style={{
                      fontSize: 11,
                      color: "var(--nx-blue)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : unitsLoading ? (
                <div style={{ ...inp, color: "var(--nx-text-muted)" }}>
                  Loading units…
                </div>
              ) : unitsError ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#EF4444" }}>
                    Failed to load units.
                  </span>
                  <button
                    type="button"
                    onClick={() => loadUnits()}
                    style={{
                      fontSize: 12,
                      color: "var(--nx-blue)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <SearchSelect
                  value={form.unitId}
                  onChange={(v) => setForm((p) => ({ ...p, unitId: v }))}
                  options={unitOptions}
                  placeholder={
                    unitOptions.length === 0
                      ? "No units available"
                      : "Search units…"
                  }
                  required
                  disabled={unitOptions.length === 0}
                />
              )}
            </div>
            <div>
              <label style={lbl}>Type *</label>
              <select
                name="type"
                value={form.type}
                onChange={change}
                style={inp}
              >
                <option value="owner">Owner</option>
                <option value="tenant">Tenant</option>
                <option value="family">Family Member</option>
              </select>
            </div>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={change}
                placeholder="resident@email.com"
                style={inp}
              />
            </div>
            <div>
              <label style={lbl}>Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={change}
                placeholder="+91 9884413814"
                style={inp}
              />
            </div>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Move-in Date</label>
              <input
                type="date"
                name="moveInDate"
                value={form.moveInDate}
                onChange={change}
                style={inp}
              />
            </div>
            <div>
              <label style={lbl}>Move-out Date</label>
              <input
                type="date"
                name="moveOutDate"
                value={form.moveOutDate}
                onChange={change}
                style={inp}
              />
            </div>
          </div>

          <div>
            <label style={lbl}>Status</label>
            <select
              value={active ? "true" : "false"}
              onChange={(e) => setActive(e.target.value === "true")}
              style={inp}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </Modal>
      )}

      {/* ── Send Invite Modal ── */}
      {inviteResident && (
        <Modal
          title={`Send Invite — ${inviteResident.fullName}`}
          submitLabel={inviteSaving ? "Sending…" : "Send Invite"}
          error={inviteError}
          isLoading={inviteSaving}
          onClose={() => setInviteResident(null)}
          onSubmit={handleSendInvite}
        >
          <div
            style={{
              fontSize: 12,
              color: "var(--nx-text-muted)",
              padding: "8px 12px",
              background: "var(--nx-surface-3)",
              borderRadius: 8,
              lineHeight: 1.5,
            }}
          >
            An email will be sent to{" "}
            <strong style={{ color: "var(--nx-text-primary)" }}>
              {inviteResident.email || "—"}
            </strong>{" "}
            with a link for the resident to create their own password and access
            the portal.
          </div>

          {(inviteResident.username || inviteResident.inviteSentAt) && (
            <div
              style={{
                fontSize: 11,
                color: "#D97706",
                padding: "6px 10px",
                background: "rgba(245,158,11,0.06)",
                borderRadius: 6,
              }}
            >
              {inviteResident.username
                ? "This resident has already activated their account. Sending a new invite will let them reset access."
                : `Invite already sent on ${new Date(inviteResident.inviteSentAt!).toLocaleDateString()}. Sending again will generate a fresh link.`}
            </div>
          )}

          <div
            style={{ borderTop: "1px solid var(--nx-border)", paddingTop: 14 }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--nx-text-secondary)",
                marginBottom: 12,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Your Email Credentials
            </p>

            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Your Email Address *</label>
              <input
                type="email"
                value={inviteForm.secretaryEmail}
                onChange={(e) =>
                  setInviteForm((p) => ({
                    ...p,
                    secretaryEmail: e.target.value,
                  }))
                }
                required
                placeholder="secretary@example.com"
                style={inp}
                autoFocus
              />
            </div>

            <div>
              <label style={lbl}>Your Email Password *</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showInvitePwd ? "text" : "password"}
                  value={inviteForm.secretaryPassword}
                  onChange={(e) =>
                    setInviteForm((p) => ({
                      ...p,
                      secretaryPassword: e.target.value,
                    }))
                  }
                  required
                  placeholder="••••••••••"
                  style={{ ...inp, paddingRight: 36 }}
                />
                <button
                  type="button"
                  onClick={() => setShowInvitePwd((p) => !p)}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--nx-text-muted)",
                    display: "flex",
                  }}
                >
                  {showInvitePwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--nx-text-muted)",
                  marginTop: 5,
                }}
              >
                Used only to send this email. Not stored.
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Bulk Send Invites Modal ── */}
      {showBulkModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={() => {
              if (!bulkSaving) setShowBulkModal(false);
            }}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(8,14,29,0.75)",
              backdropFilter: "blur(4px)",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              maxWidth: 720,
              background: "var(--nx-surface-1)",
              borderRadius: 16,
              border: "1px solid var(--nx-border)",
              boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "90vh",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 24px",
                borderBottom: "1px solid var(--nx-border)",
                flexShrink: 0,
              }}
            >
              <div>
                <h2
                  style={{
                    fontFamily: "Syne, sans-serif",
                    fontSize: 15,
                    fontWeight: 700,
                    color: "var(--nx-text-primary)",
                  }}
                >
                  {bulkDone ? "Invites Sent" : "Send Portal Invites"}
                </h2>
                {!bulkDone && (
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--nx-text-muted)",
                      marginTop: 2,
                    }}
                  >
                    {noInviteCount} not yet invited · {selectedCount} selected
                    {noEmailCount > 0 &&
                      ` · ${noEmailCount} resident${noEmailCount !== 1 ? "s" : ""} skipped (no email)`}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!bulkSaving) setShowBulkModal(false);
                }}
                style={{
                  width: 28,
                  height: 28,
                  border: "none",
                  background: "var(--nx-surface-3)",
                  borderRadius: 7,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--nx-text-muted)",
                }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Body */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {bulkDone ? (
                <div style={{ padding: 24 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "12px 16px",
                      background: "rgba(16,185,129,0.08)",
                      border: "1px solid rgba(16,185,129,0.2)",
                      borderRadius: 10,
                    }}
                  >
                    <CheckCircle2
                      size={16}
                      style={{ color: "#10B981", flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 13, color: "#10B981" }}>
                      Invite email{bulkSentCount !== 1 ? "s" : ""} sent to{" "}
                      {bulkSentCount} resident{bulkSentCount !== 1 ? "s" : ""}.
                      They will receive a link to set their password and access
                      the portal.
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Resident selection */}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      padding: "10px 24px",
                      borderBottom: "1px solid var(--nx-border)",
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setBulkEntries((p) =>
                          p.map((e) => ({
                            ...e,
                            selected:
                              !e.resident.username && !e.resident.inviteSentAt,
                          })),
                        )
                      }
                      style={{
                        fontSize: 11,
                        color: "var(--nx-blue)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      Select not yet invited
                    </button>
                    <span style={{ color: "var(--nx-border)" }}>|</span>
                    <button
                      type="button"
                      onClick={() =>
                        setBulkEntries((p) =>
                          p.map((e) => ({ ...e, selected: true })),
                        )
                      }
                      style={{
                        fontSize: 11,
                        color: "var(--nx-blue)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      Select all
                    </button>
                    <span style={{ color: "var(--nx-border)" }}>|</span>
                    <button
                      type="button"
                      onClick={() =>
                        setBulkEntries((p) =>
                          p.map((e) => ({ ...e, selected: false })),
                        )
                      }
                      style={{
                        fontSize: 11,
                        color: "var(--nx-blue)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      Deselect all
                    </button>
                  </div>

                  {bulkEntries.length === 0 ? (
                    <div
                      style={{
                        padding: "32px 24px",
                        textAlign: "center",
                        fontSize: 13,
                        color: "var(--nx-text-muted)",
                      }}
                    >
                      No residents with an email address on file. Edit residents
                      to add email addresses first.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 12,
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              borderBottom: "1px solid var(--nx-border)",
                            }}
                          >
                            <th
                              style={{
                                padding: "8px 10px 8px 24px",
                                width: 36,
                              }}
                            />
                            {["Resident", "Email", "Status"].map((h) => (
                              <th
                                key={h}
                                style={{
                                  textAlign: "left",
                                  padding: "8px 10px",
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: "var(--nx-text-muted)",
                                  letterSpacing: "0.05em",
                                  textTransform: "uppercase",
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {bulkEntries.map((e) => (
                            <tr
                              key={e.resident.id}
                              style={{
                                borderBottom: "1px solid var(--nx-border)",
                                opacity: e.selected ? 1 : 0.45,
                                transition: "opacity 0.15s",
                              }}
                            >
                              <td style={{ padding: "8px 10px 8px 24px" }}>
                                <input
                                  type="checkbox"
                                  checked={e.selected}
                                  onChange={(ev) =>
                                    setBulkEntries((p) =>
                                      p.map((x) =>
                                        x.resident.id === e.resident.id
                                          ? {
                                              ...x,
                                              selected: ev.target.checked,
                                            }
                                          : x,
                                      ),
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                />
                              </td>
                              <td
                                style={{
                                  padding: "8px 10px",
                                  color: "var(--nx-text-primary)",
                                  fontWeight: 500,
                                }}
                              >
                                {e.resident.fullName}
                              </td>
                              <td
                                style={{
                                  padding: "8px 10px",
                                  color: "var(--nx-text-secondary)",
                                }}
                              >
                                {e.resident.email}
                              </td>
                              <td style={{ padding: "8px 10px" }}>
                                {e.resident.username ? (
                                  <span
                                    style={{ fontSize: 10, color: "#10B981" }}
                                  >
                                    Active
                                  </span>
                                ) : e.resident.inviteSentAt ? (
                                  <span
                                    style={{ fontSize: 10, color: "#D97706" }}
                                  >
                                    Invite sent
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      color: "var(--nx-text-muted)",
                                    }}
                                  >
                                    Not invited
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Secretary credentials */}
                  <div
                    style={{
                      padding: "16px 24px",
                      borderTop: "1px solid var(--nx-border)",
                      background: "var(--nx-surface-2)",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--nx-text-secondary)",
                        marginBottom: 12,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Your Email Credentials
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                      }}
                    >
                      <div>
                        <label style={lbl}>Your Email Address *</label>
                        <input
                          type="email"
                          value={bulkForm.secretaryEmail}
                          onChange={(e) =>
                            setBulkForm((p) => ({
                              ...p,
                              secretaryEmail: e.target.value,
                            }))
                          }
                          placeholder="secretary@example.com"
                          style={inp}
                        />
                      </div>
                      <div>
                        <label style={lbl}>Your Email Password *</label>
                        <div style={{ position: "relative" }}>
                          <input
                            type={showBulkPwd ? "text" : "password"}
                            value={bulkForm.secretaryPassword}
                            onChange={(e) =>
                              setBulkForm((p) => ({
                                ...p,
                                secretaryPassword: e.target.value,
                              }))
                            }
                            placeholder="••••••••••"
                            style={{ ...inp, paddingRight: 36 }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowBulkPwd((p) => !p)}
                            style={{
                              position: "absolute",
                              right: 10,
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "var(--nx-text-muted)",
                              display: "flex",
                            }}
                          >
                            {showBulkPwd ? (
                              <EyeOff size={14} />
                            ) : (
                              <Eye size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--nx-text-muted)",
                        marginTop: 8,
                      }}
                    >
                      Used only to send these emails. Not stored.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "14px 24px",
                borderTop: "1px solid var(--nx-border)",
                display: "flex",
                gap: 8,
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <div>
                {bulkError && (
                  <span style={{ fontSize: 12, color: "#EF4444" }}>
                    {bulkError}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {bulkDone ? (
                  <button
                    type="button"
                    onClick={() => setShowBulkModal(false)}
                    className="nx-btn nx-btn-primary nx-btn-sm"
                  >
                    Done
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowBulkModal(false)}
                      disabled={bulkSaving}
                      className="nx-btn nx-btn-outline nx-btn-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSendBulkInvites}
                      disabled={bulkSaving || selectedCount === 0}
                      className="nx-btn nx-btn-primary nx-btn-sm"
                      style={{
                        gap: 4,
                        opacity: bulkSaving || selectedCount === 0 ? 0.6 : 1,
                      }}
                    >
                      <Send size={12} />
                      {bulkSaving
                        ? "Sending…"
                        : `Send ${selectedCount} Invite${selectedCount !== 1 ? "s" : ""}`}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ResidentsPage;
